/**
 * NewsTape — Benzinga-Pro-style live headline tape.
 *
 * Reads GET /api/news/tape (persisted signals store — news_aggregator RSS,
 * Seeking Alpha, Substack, Reddit, FCA RNS, Fed speeches). Polls every 30s,
 * paused while the tab is hidden. Dense text-xs rows: relative time (ET
 * tooltip), source chip, headline link, ticker chips. New items since the
 * last poll flash briefly. Filter bar: text/ticker quick-filter ("/" focuses
 * it), source multi-select, "only my holdings" toggle (symbols from the
 * accounts all-holdings endpoint). Real collected data only — no fabricated
 * headlines or timestamps.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { clsx } from 'clsx'
import { ExternalLink, RefreshCw, Search, X } from 'lucide-react'
import { api } from '@/lib/api'
import TickerLink from '@/components/ui/TickerLink'

// ── Types (endpoint contract) ────────────────────────────────────────────────

interface TapeItem {
  headline: string
  url: string | null
  source: string
  store_source: string
  tickers: string[]
  collected_at: string | null
  published_at: string | null
  sentiment: number | null
  signal_type: string | null
}

interface TapeSourceHealth {
  source: string
  count: number
  newest: string | null
}

interface TapeResponse {
  items?: TapeItem[]
  total?: number
  sources?: TapeSourceHealth[]
  error?: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const POLL_MS = 30_000
const MAX_ROWS = 200

function itemKey(item: TapeItem): string {
  return `${item.url ?? ''}|${item.headline}`.toLowerCase().slice(0, 160)
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  const secs = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000))
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

const ET_FORMAT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  timeZoneName: 'short',
})

function etTooltip(iso: string | null): string {
  if (!iso) return 'collection time unknown'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'collection time unknown'
  return `collected ${ET_FORMAT.format(d)}`
}

function sentimentDotClass(sentiment: number | null): string {
  if (sentiment == null || Math.abs(sentiment) < 0.05) return 'bg-slate-500'
  return sentiment > 0 ? 'bg-emerald-400' : 'bg-rose-400'
}

// ── Component ────────────────────────────────────────────────────────────────

export default function NewsTape() {
  const [items, setItems] = useState<TapeItem[]>([])
  const [feedHealth, setFeedHealth] = useState<TapeSourceHealth[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastPoll, setLastPoll] = useState<Date | null>(null)

  // Filters
  const [query, setQuery] = useState('')
  const [excludedSources, setExcludedSources] = useState<Set<string>>(new Set())
  const [holdingsOnly, setHoldingsOnly] = useState(false)
  const [holdingsSymbols, setHoldingsSymbols] = useState<string[] | null>(null)

  // New-item highlight bookkeeping
  const seenKeysRef = useRef<Set<string>>(new Set())
  const [newKeys, setNewKeys] = useState<Set<string>>(new Set())
  const firstLoadRef = useRef(true)

  const searchRef = useRef<HTMLInputElement>(null)

  // ── Holdings symbols (lazy, fetched once when the toggle is first used) ──
  const ensureHoldings = useCallback(async () => {
    if (holdingsSymbols !== null) return
    try {
      const { data } = await api.get('/api/accounts/all-holdings')
      const syms = Array.from(new Set(
        ((data?.holdings ?? []) as Array<{ symbol?: string | null }>)
          .map(h => (h?.symbol ?? '').trim().toUpperCase())
          .filter(Boolean),
      ))
      setHoldingsSymbols(syms)
    } catch {
      setHoldingsSymbols([]) // fail-soft: toggle simply matches nothing
    }
  }, [holdingsSymbols])

  // ── Polling ──────────────────────────────────────────────────────────────
  const fetchTape = useCallback(async () => {
    if (document.hidden) return // paused while tab is hidden
    try {
      const params: Record<string, string | number> = { limit: MAX_ROWS }
      if (holdingsOnly && holdingsSymbols && holdingsSymbols.length > 0) {
        params.tickers = holdingsSymbols.slice(0, 50).join(',')
      }
      const { data } = await api.get<TapeResponse>('/api/news/tape', { params })
      const next = (data?.items ?? []).filter(i => i?.headline)

      // Highlight items not seen in the previous poll (skip the first load)
      const prevSeen = seenKeysRef.current
      const fresh = new Set<string>()
      if (!firstLoadRef.current) {
        for (const it of next) {
          const k = itemKey(it)
          if (!prevSeen.has(k)) fresh.add(k)
        }
      }
      seenKeysRef.current = new Set(next.map(itemKey))
      firstLoadRef.current = false
      if (fresh.size > 0) {
        setNewKeys(fresh)
        window.setTimeout(() => setNewKeys(new Set()), 6_000)
      }

      setItems(next.slice(0, MAX_ROWS))
      setFeedHealth(data?.sources ?? [])
      setError(data?.error ?? null)
      setLastPoll(new Date())
    } catch (e) {
      setError(String(e))
    } finally {
      setLoading(false)
    }
  }, [holdingsOnly, holdingsSymbols])

  useEffect(() => {
    fetchTape()
    const id = setInterval(fetchTape, POLL_MS)
    // Refresh immediately when the tab becomes visible again
    const onVisible = () => { if (!document.hidden) fetchTape() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [fetchTape])

  // "/" focuses the filter input (unless already typing somewhere)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/') return
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement | null)?.isContentEditable) return
      e.preventDefault()
      searchRef.current?.focus()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // ── Client-side filtering ────────────────────────────────────────────────
  const sourceCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const it of items) {
      const s = it.source || 'unknown'
      counts.set(s, (counts.get(s) ?? 0) + 1)
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [items])

  const holdingsSet = useMemo(
    () => new Set((holdingsSymbols ?? []).map(s => s.toUpperCase())),
    [holdingsSymbols],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter(it => {
      if (excludedSources.has(it.source || 'unknown')) return false
      if (holdingsOnly) {
        if (holdingsSet.size === 0) return false
        if (!(it.tickers ?? []).some(t => holdingsSet.has(t.toUpperCase()))) return false
      }
      if (q) {
        const inTickers = (it.tickers ?? []).some(t => t.toLowerCase() === q || t.toLowerCase().startsWith(q))
        const inHeadline = (it.headline ?? '').toLowerCase().includes(q)
        if (!inTickers && !inHeadline) return false
      }
      return true
    })
  }, [items, query, excludedSources, holdingsOnly, holdingsSet])

  const toggleSource = (source: string) => {
    setExcludedSources(prev => {
      const next = new Set(prev)
      if (next.has(source)) next.delete(source)
      else next.add(source)
      return next
    })
  }

  const toggleHoldings = async () => {
    if (!holdingsOnly) await ensureHoldings()
    setHoldingsOnly(v => !v)
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="bg-surface-card border border-border rounded-lg p-2 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[140px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder='Filter ticker or headline — press "/"'
              aria-label="Filter news tape"
              className="w-full bg-surface-hover/50 border border-border rounded-md pl-8 pr-8 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-blue/50"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear filter"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              >
                <X size={12} aria-hidden />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={toggleHoldings}
            className={clsx(
              'px-2.5 py-1.5 rounded-md text-[11px] font-medium border transition-colors whitespace-nowrap',
              holdingsOnly
                ? 'bg-blue/15 text-blue-light border-blue/30'
                : 'text-text-secondary border-border hover:text-text-primary hover:bg-surface-hover',
            )}
          >
            Only my holdings
          </button>
          <button
            type="button"
            onClick={fetchTape}
            className="p-1.5 rounded-md border border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
            aria-label="Refresh tape"
            title={lastPoll ? `Last poll ${lastPoll.toLocaleTimeString()} — auto every 30s (paused when tab hidden)` : 'Refresh'}
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
          </button>
        </div>

        {/* Source multi-select chips (click to exclude/include) */}
        {sourceCounts.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            {sourceCounts.slice(0, 14).map(([source, count]) => (
              <button
                key={source}
                type="button"
                onClick={() => toggleSource(source)}
                className={clsx(
                  'px-2 py-0.5 rounded-full text-[10px] border transition-colors',
                  excludedSources.has(source)
                    ? 'text-text-muted border-border line-through opacity-50'
                    : 'text-text-secondary border-border hover:text-text-primary hover:bg-surface-hover',
                )}
                title={excludedSources.has(source) ? `Show ${source}` : `Hide ${source}`}
              >
                {source} <span className="text-text-muted">{count}</span>
              </button>
            ))}
            <span className="ml-auto text-[10px] text-text-muted whitespace-nowrap">
              {filtered.length} of {items.length} headlines
            </span>
          </div>
        )}
      </div>

      {/* Feed health strip (per originator feed: count + newest) */}
      {feedHealth.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap px-1 text-[10px] text-text-muted">
          <span className="uppercase tracking-wider font-semibold">Feeds</span>
          {feedHealth.map(f => (
            <span key={f.source} className="whitespace-nowrap" title={etTooltip(f.newest)}>
              {f.source} · {f.count} · {relativeTime(f.newest)}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="text-[11px] text-amber-400 px-1" role="status">
          Tape degraded: {error}
        </div>
      )}

      {/* The tape */}
      <div className="card overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="px-4 py-10 text-center text-text-muted text-sm">Loading tape…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-text-muted text-sm">
            {items.length === 0
              ? 'No headlines collected yet — the signal loop refreshes every ~30 min.'
              : 'No headlines match the current filters.'}
          </div>
        ) : (
          <ul className="divide-y divide-border/60" aria-label="Live news tape">
            {filtered.map(item => {
              const k = itemKey(item)
              return (
                <li
                  key={k}
                  className={clsx(
                    'px-3 py-1.5 flex items-start gap-2 transition-colors hover:bg-surface-hover/50',
                    newKeys.has(k) && 'bg-blue/10 animate-pulse',
                  )}
                >
                  <span
                    className="w-9 shrink-0 text-right text-[10px] font-mono text-text-muted leading-5 cursor-default"
                    title={etTooltip(item.collected_at)}
                  >
                    {relativeTime(item.collected_at)}
                  </span>
                  <span
                    className={clsx('w-1.5 h-1.5 rounded-full shrink-0 mt-2', sentimentDotClass(item.sentiment))}
                    title={item.sentiment != null ? `sentiment ${item.sentiment.toFixed(2)}` : 'no sentiment'}
                  />
                  <span
                    className="badge badge-slate text-[9px] shrink-0 uppercase tracking-wide max-w-[92px] truncate mt-0.5"
                    title={`${item.source} (${item.store_source})`}
                  >
                    {item.source}
                  </span>
                  <div className="flex-1 min-w-0 flex items-baseline gap-2 flex-wrap">
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs leading-5 text-text-primary hover:text-blue-light transition-colors"
                      >
                        {item.headline}
                        <ExternalLink size={10} className="inline-block ml-1 opacity-50" aria-hidden />
                      </a>
                    ) : (
                      <span className="text-xs leading-5 text-text-primary">{item.headline}</span>
                    )}
                    {(item.tickers ?? []).slice(0, 4).map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setQuery(t)}
                        className="shrink-0"
                        title={`Filter tape to ${t}`}
                      >
                        <TickerLink ticker={t} className="text-[10px] pointer-events-none" />
                      </button>
                    ))}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
